import { getDefinition } from "./registry.js";

/**
 * Named parts (TOM-618, a local change: see VENDORED.md). Every visible piece
 * of the faces Neo uses carries `data-part`, so a page's CSS can restyle it:
 * `[data-part="value"]{fill:#ffd60a}`. The names follow numeric-designs.js and
 * wind-designs.js. Parts sit on the shapes and text themselves, never on a
 * `<g transform>`: a CSS `transform` on an element with a `transform`
 * attribute replaces that attribute. Every part carries its own centre as a
 * `transform-origin` presentation attribute (part-origins.js, TOM-587), so a
 * plain `transform:rotate(90deg)` turns it in place in every browser; a
 * page's own transform-origin rule still wins.
 */
export const partDescriptions = Object.freeze({
  background: "The card's backdrop behind the whole face.",
  icon: "The small symbol beside the title.",
  title: "The title.",
  subtitle: "The line under the face naming it.",
  footer: "The source and age line along the bottom.",
  status: "The quality word in the bottom corner, such as STALE.",
  face: "A plate behind a reading: the disc at a dial's centre, an arc gauge's face, a number's capsule or digit windows.",
  dial: "A dial's disc and rings, and the empty ring shown while a source is missing.",
  ticks: "Scale marks, and a chart's grid lines.",
  "tick-labels":
    "Numbers along a scale: a dial's degrees, a gauge's scale values, a chart's values and times.",
  cardinals: "N, E, S and W on a compass dial.",
  track:
    "The empty channel a reading moves along: a gauge's arc, a meter's bar, a tank's body, the rudder's bar.",
  fill: "The part of the track filled up to the reading, and a tank's liquid and its surface.",
  zones: "Coloured bands marking ranges on a scale.",
  needle:
    "The main pointer on a dial; on the wind rose, the apparent wind's pointer.",
  pointer:
    "A second pointer on a dial (the wind rose's true wind), or the marker at the reading on a bar or arc: an arc gauge's dot, a meter's knob, the rudder's blade.",
  ribbon: "The soft wedge or arc behind a needle or pointer.",
  reference:
    "A fixed mark on the scale: a target, threshold, ordered angle, low-level line or zero line.",
  value: "The main reading.",
  unit: "A reading's unit.",
  secondary:
    "Another reading beside the main one, such as the other wind speed, a pointer's angle, a tank's quantity under its percent or the rudder's ordered angle.",
  delta: "The change over the history window.",
  caption:
    "Small words that label or explain: labels above a value, BOW, PORT and STARBOARD, the reference line along the bottom, range summaries.",
  low: "The label at a scale's low end (the rudder's PORT), and a tank's LOW mark.",
  high: "The label at a scale's high end (the rudder's STARBOARD).",
  "history-line": "The history trace.",
  "history-area":
    "The shaded area under the history trace, or the ribbon design's bars.",
  "history-dot":
    "The dot on the latest history point, and the dots of a wind trail.",
  legend: "A chart's legend swatches and names.",
  badge: "The small pill naming a chart's window, such as 24h.",
  boat: "The boat's outline.",
  sail: "The sails.",
  rule: "A plain line: the accent stroke under a number, the divider's line and its end dot, a separator.",
  fade: "The shading over the top and bottom of the rolling digits.",
});

// Every face's card: the background, and the title, subtitle, footer and
// status a panel shows when its options ask for them.
const shell = ["background", "icon", "title", "subtitle", "footer", "status"];
// What a face draws while its source is missing (render-kit.js missing()).
const unavailable = ["dial", "value", "caption"];
const numberParts = [
  "face",
  "fade",
  "rule",
  "value",
  "unit",
  "delta",
  "caption",
  "history-line",
  "history-area",
  "history-dot",
];
const chartParts = [
  "value",
  "unit",
  "caption",
  "badge",
  "ticks",
  "tick-labels",
  "history-line",
  "history-area",
  "history-dot",
  "legend",
  "reference",
];
const faces = {
  number: [...numberParts, "secondary"],
  "trend-number": numberParts,
  "arc-gauge": [
    "face",
    "track",
    "zones",
    "fill",
    "pointer",
    "ticks",
    "tick-labels",
    "reference",
    "value",
    "unit",
  ],
  "needle-gauge": [
    "face",
    "track",
    "zones",
    "fill",
    "ribbon",
    "needle",
    "ticks",
    "tick-labels",
    "value",
    "unit",
  ],
  "vertical-meter": [
    "track",
    "zones",
    "fill",
    "ticks",
    "reference",
    "value",
    "unit",
    "caption",
    "low",
    "high",
  ],
  "horizontal-meter": [
    "track",
    "zones",
    "fill",
    "pointer",
    "reference",
    "value",
    "unit",
    "caption",
    "low",
    "high",
  ],
  tank: [
    "track",
    "fill",
    "ticks",
    "reference",
    "low",
    "value",
    "secondary",
    "unit",
    "caption",
  ],
  compass: [
    "dial",
    "face",
    "ticks",
    "tick-labels",
    "cardinals",
    "zones",
    "ribbon",
    "needle",
    "value",
    "unit",
    "caption",
  ],
  "wind-rose": [
    "dial",
    "face",
    "ticks",
    "tick-labels",
    "cardinals",
    "zones",
    "ribbon",
    "needle",
    "pointer",
    "history-dot",
    "boat",
    "sail",
    "value",
    "unit",
    "secondary",
    "caption",
    "rule",
  ],
  "wind-angle": [
    "dial",
    "ticks",
    "zones",
    "ribbon",
    "needle",
    "value",
    "unit",
    "caption",
  ],
  rudder: [
    "track",
    "fill",
    "ticks",
    "reference",
    "pointer",
    "value",
    "secondary",
    "caption",
    "low",
    "high",
  ],
  divider: ["rule", "caption"],
  // The sparkline's endpointLabel is the latest value at the trace's end.
  sparkline: [...chartParts, "secondary"],
  history: chartParts,
};
// An arc or needle gauge and a history keep their face with no reading
// (render.js, TOM-665): a gauge's scale is always valid, and a history with
// nothing to draw says so in its own value and caption, so none draws the
// unavailable ring. A number keeps its face too, a quiet dash (TOM-956).
const keepsFace = new Set([
  "arc-gauge",
  "divider",
  "history",
  "needle-gauge",
  "number",
]);
const table = new Map(
  Object.entries(faces).map(([id, own]) => [
    id,
    Object.freeze(
      [
        ...new Set([
          ...shell,
          ...own,
          ...(keepsFace.has(id) ? [] : unavailable),
        ]),
      ].sort(),
    ),
  ]),
);
const shellOnly = Object.freeze([...shell].sort());

/**
 * The part names a control's face can show, in any design and with any of its
 * options. Faces outside the fourteen Neo uses are not named yet: theirs is
 * the card's own list only.
 */
export function controlParts(definitionId) {
  getDefinition(definitionId);
  return table.get(definitionId) ?? shellOnly;
}
